/**
 * Prompts and read-text builders for the hands-free voice co-pilot (game app).
 *
 * Reading is now done by a dedicated TTS endpoint (see /api/game/tts), so these
 * builders return PLAIN text to be spoken. The Realtime API is used only for
 * LISTENING to commands (text output, no audio), driven by NAV_LISTEN_INSTRUCTIONS.
 *
 * The read text is assembled here from the SAME strings the screen renders, so
 * what the user hears matches what they would read.
 */

import type { EpisodeSummary } from '../services/GameApiService';

/**
 * Listening-only persona for the Realtime session. It does NOT read text (TTS
 * handles that). Its only job is to map what the driver says to a navigation
 * tool. Output is text-only; the model should call a function, not speak.
 */
export const NAV_LISTEN_INSTRUCTIONS = `És o reconhecedor de comandos de voz de uma app usada por alguém que está a CONDUZIR. Tudo é em Português Europeu de Portugal.

A tua ÚNICA função é ouvir o que a pessoa diz e chamar a função apropriada. NUNCA respondas com fala nem com texto longo — chama sempre uma das funções disponíveis.

Regras:
- Para escolher uma pessoa a entrevistar, a pessoa pode dizer só o primeiro nome, o apelido ou o cargo. Faz a correspondência ao id certo e chama select_character com esse id.
- "começar", "vamos", "iniciar", "ok", "sim", "avança" → confirm_start.
- "próximo episódio", "continuar", "avançar" → next_episode.
- "resolver o caso", "iniciar resolução", "já sei quem foi" → resolve_case.
- "repetir", "lê outra vez", "não percebi" → read_again.
- Se não perceberes ou for irrelevante, não chames nenhuma função.`;

/** Episode-ready screen: situation + who you'll talk to + your role.
 * `situation` is only present on episode 1 (the opener). From episode 2 on, that
 * text is the bridge and is read on the waiting screen, so it is omitted here. */
export function buildEpisodeReadyReadText(args: {
  title: string;
  situation?: string;
  characterName?: string;
  aiRole: string;
  characterDescription?: string;
  playerRole: string;
}): string {
  const who = args.characterName ? `${args.characterName} — ${args.aiRole}` : args.aiRole;
  const parts = [args.title, ''];
  if (args.situation && args.situation.trim() !== '') {
    parts.push(`Situação: ${args.situation}`, '');
  }
  parts.push(`Vais falar com ${who}.`);
  if (args.characterDescription) parts.push(args.characterDescription);
  parts.push('', `O teu papel: ${args.playerRole}`);
  parts.push('', 'Diz "começar" quando estiveres pronto.');
  return parts.join('\n');
}

/** Waiting screen: read the candidate list and ask who to interview next. */
export function buildCandidateChoiceReadText(
  candidates: Array<{ name: string; role: string }>,
  allowResolve: boolean,
): string {
  const parts = ['Escolhe a próxima pessoa a entrevistar. Podes falar com:'];
  candidates.forEach((c) => parts.push(`- ${c.name}, ${c.role}.`));
  parts.push('', 'Diz o nome de quem queres entrevistar.');
  if (allowResolve) {
    parts.push('Ou diz "resolver o caso" se já tens pistas suficientes.');
  }
  return parts.join('\n');
}

/**
 * Waiting screen, recap visible but next episode still loading.
 * Reads the recap only, no "next episode ready" prompt yet.
 */
export function buildRecapReadText(args: {
  recap: { characterName: string; summary: EpisodeSummary };
}): string {
  const parts: string[] = [];
  parts.push(`Resumo da conversa com ${args.recap.characterName}.`, '', args.recap.summary.narrativeRecap);
  if (args.recap.summary.keyMoments && args.recap.summary.keyMoments.length > 0) {
    parts.push('', 'Momentos-chave:');
    args.recap.summary.keyMoments.forEach((m) => parts.push(`- ${m}`));
  }
  return parts.join('\n');
}

/**
 * Waiting screen, after selection: the next episode is ready. Reads the recap
 * (narrative + key moments) if available, then prompts to continue.
 */
export function buildWaitingReadyReadText(args: {
  isResolve: boolean;
  recap?: { characterName: string; summary: EpisodeSummary };
  bridgeText?: string | null;
}): string {
  const parts: string[] = [];
  if (args.recap) {
    parts.push(`Resumo da conversa com ${args.recap.characterName}.`, '', args.recap.summary.narrativeRecap);
    if (args.recap.summary.keyMoments && args.recap.summary.keyMoments.length > 0) {
      parts.push('', 'Momentos-chave:');
      args.recap.summary.keyMoments.forEach((m) => parts.push(`- ${m}`));
    }
    parts.push('');
  }
  parts.push(buildReadyTailReadText({ isResolve: args.isResolve, bridgeText: args.bridgeText }));
  return parts.join('\n');
}

/**
 * The short "next episode is ready" prompt on its own. Spoken when the recap was
 * already read (or is still playing) and the episode becomes ready — so the
 * recap is never restarted, only this tail is appended. The bridge into the next
 * encounter rides here: it only exists once the episode is ready, and this tail is
 * the one segment guaranteed to be spoken in both the full-read and continues paths.
 */
export function buildReadyTailReadText(args: { isResolve: boolean; bridgeText?: string | null }): string {
  const parts: string[] = [];
  if (args.bridgeText && args.bridgeText.trim() !== '') {
    parts.push(args.bridgeText, '');
  }
  const what = args.isResolve ? 'A conversa de resolução do caso' : 'O próximo episódio';
  parts.push(`${what} está pronto. Diz "continuar" para avançar.`);
  return parts.join('\n');
}
